import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { LightningIcon, ChartLineUpIcon, BrainIcon, PlayCircleIcon } from "@phosphor-icons/react";

const Login = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: ChartLineUpIcon, title: "REAL-TIME TELEMETRY", desc: "Live subscribers, views & per-video stats" },
    { icon: BrainIcon, title: "AI POST ANALYSIS", desc: "Understand why each post performs" },
    { icon: PlayCircleIcon, title: "NEXT 3 MISSIONS", desc: "AI-suggested videos based on your data" },
  ];

  return (
    <div className="relative min-h-screen bg-[#050505] grid-texture overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1591981864041-afda2ddf5031?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxmdXR1cmlzdGljJTIwZGFyayUyMGdyZWVuJTIwdGVjaG5vbG9neSUyMGFic3RyYWN0fGVufDB8fHxncmVlbnwxNzg2NzQ5NDc0fDA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-[0.10] pointer-events-none"
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#16C784]/60 to-transparent" />

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 min-h-screen flex flex-col">
        <header className="py-8 flex items-center gap-3">
          <img src="/favicon.svg" alt="Content Overwatch" className="w-10 h-10" />
          <span className="font-rajdhani font-bold text-lg tracking-[0.2em] uppercase">
            CONTENT<span className="text-[#16C784]"> OVERWATCH</span>
          </span>
        </header>

        <div className="flex-1 grid lg:grid-cols-2 gap-16 items-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#A3E635] mb-6">
              // YouTube Command Center
            </div>
            <h1 className="font-rajdhani text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-white text-glow">
              YOUR CHANNEL,<br />
              <span className="text-[#16C784]">FULLY WEAPONIZED.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#A0A0AB] max-w-lg leading-relaxed">
              A real-time dashboard for your YouTube empire. Track views, subscribers and every
              upload — then let AI dissect why each video performs and command your next 3 moves.
            </p>

            <button
              data-testid="login-button"
              onClick={handleLogin}
              className="group mt-10 inline-flex items-center gap-3 bg-[#16C784] hover:bg-[#0FA968] text-white font-rajdhani font-bold uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-none transition-colors duration-300 arc-glow"
            >
              <LightningIcon size={18} weight="fill" />
              Initialize · Sign in with Google
            </button>
            <p className="mt-4 font-mono text-[11px] text-[#5a5a63] tracking-wider">
              SECURE OAUTH LINK · NO PASSWORD STORED
            </p>
          </motion.div>

          <div className="space-y-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="flex items-start gap-5 bg-[#0C0C0E] border border-[#222226] p-6 hover:border-[#16C784]/50 transition-colors duration-300"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}
              >
                <div className="w-11 h-11 shrink-0 rounded-full border border-[#A3E635]/40 flex items-center justify-center">
                  <f.icon size={22} className="text-[#A3E635]" />
                </div>
                <div>
                  <div className="font-rajdhani font-bold tracking-[0.15em] text-white">{f.title}</div>
                  <div className="text-sm text-[#A0A0AB] mt-1">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
